import React, { useEffect, useState } from "react";
import { useQuery, useSubscription, useLazyQuery, gql } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import Loading from "../../components/Loading";
import Moment from "moment";
import "moment-timezone";
import {
  GetSitesForStatusQuery,
  GetSiteConnectionEventsQuery,
  Maybe,
} from "../../gql/graphql";

export const SITES = gql`
  query GetSitesForStatus {
    sites {
      items {
        id
        name
        lgaName
        connectionId
        isActive
        company {
          id
          name
        }
      }
    }
  }
`;

const SITE_CONNECTION_EVENTS = gql`
  query GetSiteConnectionEvents($siteId: UUID!, $take: Int) {
    siteConnectionEvents(
      where: { siteId: { eq: $siteId } }
      order: { createdUtc: DESC }
      take: $take
    ) {
      items {
        id
        siteId
        connectionId
        eventType
        createdUtc
        ipAddress
        disconnectReason
      }
      totalCount
    }
  }
`;

const UPDATES = gql`
  subscription SiteStatusUpdated {
    siteUpdated {
      id
      name
      lgaName
      connectionId
      isActive
    }
  }
`;

type NamedEntity = { name?: Maybe<string>; id?: Maybe<string> };
function compare(a: NamedEntity | null, b: NamedEntity | null) {
  if (!a || !a.name) return -1;
  if (!b || !b.name) return 1;

  if (a.name < b.name) return -1;
  if (a.name > b.name) return 1;

  if (!a.id) return -1;
  if (!b.id) return 1;

  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;

  return 0;
}

type SiteList = NonNullable<NonNullable<GetSitesForStatusQuery["sites"]>["items"]>;

export const Status = () => {
  let { id } = useParams();
  const navigate = useNavigate();

  const [sites, setSites] = useState<SiteList>([]);

  const { data, loading } = useQuery<GetSitesForStatusQuery>(SITES);
  const { data: subData } = useSubscription(UPDATES);

  const [
    loadConnectionEvents,
    { data: connectionEventsData, loading: connectionEventsLoading },
  ] = useLazyQuery<GetSiteConnectionEventsQuery>(SITE_CONNECTION_EVENTS, {
    fetchPolicy: "network-only",
  });

  // Load connection events when site is selected
  useEffect(() => {
    if (id) {
      loadConnectionEvents({
        variables: { siteId: id, take: 500 },
      });
    }
  }, [id, loadConnectionEvents]);

  // Handle site updates from subscription
  useEffect(() => {
    if (subData && subData.siteUpdated) {
      const existing = sites.find(
        (site) => site?.id === subData.siteUpdated.id
      );

      if (existing) {
        existing.connectionId = subData.siteUpdated.connectionId;
        existing.isActive = subData.siteUpdated.isActive;
        existing.name = subData.siteUpdated.name;

        setSites([...sites]);
      } else {
        setSites([...sites, subData.siteUpdated]);
      }
    }
  }, [subData, sites]);

  // Initialize sites from query
  useEffect(() => {
    if (data?.sites?.items) {
      setSites(structuredClone(data.sites.items));
    }
  }, [data]);

  const connectionEvents =
    connectionEventsData?.siteConnectionEvents?.items ?? [];
  const selectedSite = sites.find((s) => s?.id === id);

  // Format duration helper
  const formatDuration = (start: Moment.Moment, end: Moment.Moment): string => {
    const dur = Moment.duration(end.diff(start));
    const days = Math.floor(dur.asDays());
    const hours = dur.hours();
    const minutes = dur.minutes();
    const seconds = dur.seconds();

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Create connection sessions by pairing Connected and Disconnected events
  type ConnectionSession = {
    id: string;
    connectedAt: string | null;
    disconnectedAt: string | null;
    duration: string | null;
    ipAddress: string | null;
    connectionId: string | null;
    disconnectReason: string | null;
    isActive: boolean;
  };

  const buildConnectionSessions = (): ConnectionSession[] => {
    const sessions: ConnectionSession[] = [];
    const usedIndices = new Set<number>();

    // Events are in DESC order, so we iterate and pair Disconnected with the next Connected
    for (let i = 0; i < connectionEvents.length; i++) {
      if (usedIndices.has(i)) continue;

      const event = connectionEvents[i];
      if (!event) continue;

      if (event.eventType === "Disconnected") {
        // Look for matching Connected event
        let connectedEvent = null;
        for (let j = i + 1; j < connectionEvents.length; j++) {
          const candidate = connectionEvents[j];
          if (candidate?.eventType === "Connected" && !usedIndices.has(j)) {
            connectedEvent = candidate;
            usedIndices.add(j);
            break;
          }
        }

        usedIndices.add(i);

        const start = connectedEvent?.createdUtc
          ? Moment.utc(connectedEvent.createdUtc)
          : null;
        const end = Moment.utc(event.createdUtc);
        const duration = start ? formatDuration(start, end) : null;

        sessions.push({
          id: event.id?.toString() ?? `session-${i}`,
          connectedAt: connectedEvent?.createdUtc ?? null,
          disconnectedAt: event.createdUtc ?? null,
          duration,
          ipAddress: connectedEvent?.ipAddress ?? null,
          connectionId: connectedEvent?.connectionId ?? event.connectionId ?? null,
          disconnectReason: event.disconnectReason ?? null,
          isActive: false,
        });
      } else if (event.eventType === "Connected") {
        // This is an active connection (no disconnect yet)
        usedIndices.add(i);

        const start = Moment.utc(event.createdUtc);
        const now = Moment.utc();
        const duration = formatDuration(start, now) + " (ongoing)";

        sessions.push({
          id: event.id?.toString() ?? `session-${i}`,
          connectedAt: event.createdUtc ?? null,
          disconnectedAt: null,
          duration,
          ipAddress: event.ipAddress ?? null,
          connectionId: event.connectionId ?? null,
          disconnectReason: null,
          isActive: true,
        });
      }
    }

    return sessions;
  };

  const sessions = buildConnectionSessions();

  // Calculate outage duration between two sessions
  const getOutageDuration = (
    currentSession: ConnectionSession,
    previousSession: ConnectionSession
  ): string | null => {
    // Current session's connected time and previous session's disconnected time
    if (!currentSession.connectedAt || !previousSession.disconnectedAt) {
      return null;
    }

    const outageStart = Moment.utc(currentSession.disconnectedAt);
    const outageEnd = Moment.utc(previousSession.connectedAt);

    if (outageEnd.isBefore(outageStart)) {
      return null; // No outage
    }

    return formatDuration(outageStart, outageEnd);
  };

  return (
    <>
      <Loading loading={loading} />

      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">Connection Events</h1>
      </div>

      {sites && (
        <>
          <div className="row">
            <div className="col-md-4 col-xl-2">
              <table className="table">
                <tbody>
                  {sites
                    .filter((s) => s?.isActive)
                    .sort(compare)
                    .map((site, index) => (
                      <tr
                        key={"site-" + index}
                        className={
                          site?.id === id
                            ? "table-dark text-light"
                            : site?.connectionId
                            ? "table-success"
                            : "table-danger"
                        }
                        onClick={() => navigate(`/status/${site?.id}`)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{site?.name}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="col-xl-10">
              {id && sites.length > 0 && (
                <div className="pb-3 mb-3">
                  <div className="mb-4">
                    <h4>
                      {selectedSite?.name} -{" "}
                      {selectedSite?.connectionId ? (
                        <span className="badge bg-success">Connected</span>
                      ) : (
                        <span className="badge bg-danger">Not Connected</span>
                      )}
                    </h4>
                    {connectionEventsData?.siteConnectionEvents?.totalCount !==
                      undefined && (
                      <small className="text-muted">
                        {sessions.length} sessions from{" "}
                        {connectionEventsData.siteConnectionEvents.totalCount}{" "}
                        events
                      </small>
                    )}
                  </div>

                  {connectionEventsLoading && <Loading loading={true} />}

                  {/* Sessions Table View */}
                  {!connectionEventsLoading && sessions.length > 0 && (
                    <div className="sessions-table">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th style={{ width: "30%" }}>Connected</th>
                            <th style={{ width: "25%" }} className="text-center">
                              Duration
                            </th>
                            <th style={{ width: "30%" }}>Disconnected</th>
                            <th style={{ width: "15%" }}>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sessions.map((session, index) => {
                            // Calculate outage from previous session (sessions are in DESC order)
                            const nextSession = sessions[index + 1];
                            const outageDuration =
                              nextSession && !session.isActive
                                ? getOutageDuration(nextSession, session)
                                : null;

                            return (
                              <React.Fragment key={session.id}>
                                {/* Session row */}
                                <tr
                                  className={
                                    session.isActive ? "table-success" : ""
                                  }
                                >
                                  <td>
                                    <span
                                      className="rounded-circle d-inline-block bg-success me-2"
                                      style={{ width: "8px", height: "8px" }}
                                    ></span>
                                    {session.connectedAt
                                      ? Moment.utc(session.connectedAt)
                                          .tz(Moment.tz.guess())
                                          .format("DD MMM HH:mm:ss")
                                      : "Unknown"}
                                    {session.ipAddress && (
                                      <small className="text-muted d-block">
                                        IP: {session.ipAddress}
                                      </small>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    <span
                                      className={`badge ${
                                        session.isActive
                                          ? "bg-success"
                                          : "bg-secondary"
                                      }`}
                                    >
                                      {session.duration ?? "—"}
                                    </span>
                                  </td>
                                  <td>
                                    {session.isActive ? (
                                      <span className="badge bg-success">
                                        Still Connected
                                      </span>
                                    ) : (
                                      <>
                                        <span
                                          className="rounded-circle d-inline-block bg-danger me-2"
                                          style={{ width: "8px", height: "8px" }}
                                        ></span>
                                        {session.disconnectedAt
                                          ? Moment.utc(session.disconnectedAt)
                                              .tz(Moment.tz.guess())
                                              .format("DD MMM HH:mm:ss")
                                          : "—"}
                                      </>
                                    )}
                                    {session.disconnectReason && (
                                      <small className="text-danger d-block">
                                        {session.disconnectReason}
                                      </small>
                                    )}
                                  </td>
                                  <td>
                                    <small
                                      className="text-muted font-monospace"
                                      style={{ fontSize: "0.7rem" }}
                                    >
                                      {session.connectionId?.substring(0, 12)}...
                                    </small>
                                  </td>
                                </tr>

                                {/* Outage row */}
                                {outageDuration && (
                                  <tr className="table-warning">
                                    <td
                                      colSpan={4}
                                      className="text-center py-1"
                                    >
                                      <small>
                                        <span className="badge bg-warning text-dark">
                                          ⚠ Outage: {outageDuration}
                                        </span>
                                      </small>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {!connectionEventsLoading && sessions.length === 0 && (
                    <div className="alert alert-info">
                      No connection events recorded for this site yet.
                    </div>
                  )}
                </div>
              )}

              {!id && (
                <div className="alert alert-secondary">
                  Select a site to view its connection events.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};
